import _ from "lodash";
import { PRICE_TYPE, PRICE_TYPE_BY_NAME } from "constants/PriceType";
import {
  RULE_FILTER,
  PPFILTER,
  PRICE,
  TYPE_FILTER,
  STATUS,
} from "../Constants";
import { getCurrencySymbol } from "helpers/Utils";

export function isSectionEmpty(section) {
  if (!section || _.isEmpty(section)) {
    return true;
  } else if (_.has(section, "includes") && _.has(section, "excludes")) {
    return _.isEmpty(section.includes) && _.isEmpty(section.excludes);
  } else if (_.has(section, "includes") && _.isEmpty(section.includes)) {
    return true;
  } else if (_.has(section, "excludes") && _.isEmpty(section.excludes)) {
    return true;
  } else {
    return false;
  }
}

export function transformRules({
  rules,
  protectedFLIds,
  preservedFLIds,
  resourceById,
}) {
  const transformedRules = _.map(rules, (rule) => {
    let targetingSection = {};
    if (rule.targetingSection.inventory) {
      if (!isSectionEmpty(rule.targetingSection.inventory.unitIds)) {
        _.set(
          targetingSection,
          ["inventory", "units"], // units not unitIds!
          rule.targetingSection.inventory.unitIds
        );
      }
      if (!isSectionEmpty(rule.targetingSection.inventory.placementIds)) {
        _.set(
          targetingSection,
          ["inventory", "placementIds"],
          rule.targetingSection.inventory.placementIds
        );
      }
      if (!isSectionEmpty(rule.targetingSection.inventory.mcm)) {
        _.set(
          targetingSection,
          ["inventory", "mcm"],
          rule.targetingSection.inventory.mcm
        );
      }
    }

    const targetingKeys = [
      "customs",
      "deviceCategory",
      "os",
      "country",
      "browser",
      "inventoryTypes",
      "videoPositions",
      "inventoryFormat",
      "inventoryUrl",
      "inventorySize",
      "mobileApplication",
    ];
    _.forEach(targetingKeys, (tk) => {
      if (!isSectionEmpty(rule.targetingSection[tk])) {
        targetingSection[tk] = rule.targetingSection[tk];
      }
    });

    rule.__isProtected =
      _.indexOf(protectedFLIds, _.parseInt(rule.externalId)) !== -1;
    rule.__isPreserved =
      _.indexOf(preservedFLIds, _.parseInt(rule.externalId)) !== -1;
    rule.__isI2wRule = _.startsWith(rule.name, "Intowow_");
    rule.__isPubRule = !_.startsWith(rule.name, "Intowow_");

    return {
      ...rule,
      targetingSection,
      props: _.get(resourceById, [rule.externalId, "props"], null),
    };
  });

  return transformedRules;
}

export function getTypeOptions(data) {
  const tempData = _.cloneDeep(data);
  return _.values(TYPE_FILTER).map((type) => {
    if (type === TYPE_FILTER.ALL)
      return { key: type, name: type, list: tempData };
    return { key: type, name: type, list: _.filter(tempData, { type }) };
  });
}

export function getRuleOptions(data) {
  const tempData = _.cloneDeep(data);
  return _.values(RULE_FILTER).map((type) => {
    if (type === RULE_FILTER.ALL)
      return { key: type, name: type, list: tempData };
    if (type === RULE_FILTER.INTOWOW_ONLY)
      return {
        key: type,
        name: type,
        list: _.filter(tempData, { __isI2wRule: true }),
      };
    if (type === RULE_FILTER.PUBLISHER_ONLY)
      return {
        key: type,
        name: type,
        list: _.filter(tempData, { __isPubRule: true }),
      };
    return {};
  });
}

export function getPPOptions(rules) {
  const tempRules = _.cloneDeep(rules);
  const protectedRules = _.remove(tempRules, { __isProtected: true });
  const preservedRules = _.remove(tempRules, { __isPreserved: true });
  return [
    {
      key: PPFILTER.ALL,
      name: "ALL",
      list: rules,
    },
    {
      key: PPFILTER.PROTECTED,
      name: "Protected",
      list: protectedRules,
    },
    {
      key: PPFILTER.PRESERVED,
      name: "Preserved",
      list: preservedRules,
    },
    {
      key: PPFILTER.NOT_PROTECTED_PRESERVED,
      name: "Not Protected or Preserved",
      list: tempRules,
    },
  ];
}

export function getPriceTypeOptions(data) {
  const tempData = _.cloneDeep(data);
  const dataGrouped = _.groupBy(tempData, (item) => {
    const setEverySetting = _.find(
      item.priceSection.priceSettings,
      (st) => st.isSetForEverything
    );
    if (setEverySetting) return _.get(setEverySetting, ["priceType"], -1);
    return -1;
  });
  const priceTypeFilters = _.map(_.keys(PRICE_TYPE), (pt) => {
    return {
      key: PRICE_TYPE[pt],
      name: PRICE_TYPE[pt],
      list: dataGrouped[pt] || [],
    };
  });
  priceTypeFilters.unshift({
    key: "ALL",
    name: "ALL",
    list: tempData,
  });

  return priceTypeFilters;
}

export function getPriceOptions(data, customPrice, currency) {
  const price = _.toNumber(customPrice);
  const isValidPrice = !_.isNaN(price);
  return [
    {
      key: PRICE.ALL,
      name: "ALL",
      list: data,
    },
    {
      key: PRICE.CUSTOM,
      name: `> ${getCurrencySymbol(currency)}`,
      list: isValidPrice
        ? data.filter((item) => {
            const setEverySetting = _.find(
              item.priceSection.priceSettings,
              (st) => st.isSetForEverything
            );

            if (!setEverySetting) return false;

            if (setEverySetting.branded === "BLOCKED") {
              return false;
            }

            if (setEverySetting.priceType === PRICE_TYPE_BY_NAME.LGO) {
              return false;
            }

            return setEverySetting.price > price;
          })
        : [],
    },
    {
      key: PRICE.BLOCKED,
      name: "Blocked",
      list: data.filter((item) => {
        const setEverySetting = _.find(
          item.priceSection.priceSettings,
          (st) => st.isSetForEverything
        );

        if (!setEverySetting) return false;

        return setEverySetting.branded === "BLOCKED";
      }),
    },
  ];
}

export function parseRuleToFilter(
  ruleData,
  currentFilter,
  { currency, customPrice }
) {
  const tempFilter = _.cloneDeep(currentFilter);

  // Stataus filter processt
  // let filteredRules =
  //   currentFilter.status === STATUS.ALL
  //     ? ruleData
  //     : _.filter(ruleData, { status: currentFilter.status });

  let filteredRules = [...ruleData];

  // Type filter process
  const typeOptions = getTypeOptions(filteredRules);
  const typeOpt = _.find(typeOptions, { key: currentFilter.type });
  if (typeOpt) filteredRules = typeOpt.list;
  else {
    filteredRules = typeOptions[0].list;
    tempFilter.type = typeOptions[0].key;
  }

  // Rule filter process
  const ruleOptions = getRuleOptions(filteredRules);
  const ruleOpt = _.find(ruleOptions, { key: currentFilter.rule });

  if (ruleOpt) filteredRules = ruleOpt.list;
  else {
    filteredRules = ruleOptions[0].list;
    tempFilter.rule = ruleOptions[0].key;
  }

  // PP filter process
  const ppOptions = getPPOptions(ruleOptions[2].list);
  const ppOpt = _.find(ppOptions, { key: currentFilter.ppType });

  if (ppOpt) filteredRules = ppOpt.list;
  else {
    filteredRules = ppOptions[0].list;
    tempFilter.ppType = ppOptions[0].key;
  }

  // Price Type filter process
  const priceTypeOptions = getPriceTypeOptions(filteredRules);
  const priceTypeOpt = _.find(priceTypeOptions, {
    key: currentFilter.priceType,
  });

  if (priceTypeOpt) filteredRules = priceTypeOpt.list;
  else {
    filteredRules = priceTypeOptions[0].list;
    tempFilter.priceType = priceTypeOptions[0].key;
  }

  // Price filter process
  const priceOptions = getPriceOptions(filteredRules, customPrice, currency);
  const priceOpt = _.find(priceOptions, {
    key: currentFilter.price,
  });

  if (priceOpt) filteredRules = priceOpt.list;
  else {
    filteredRules = priceOptions[0].list;
    tempFilter.price = priceOptions[0].key;
  }

  if (tempFilter.rule === RULE_FILTER.ALL) {
    filteredRules = ruleOptions[0].list;
  } else if (tempFilter.rule === RULE_FILTER.INTOWOW_ONLY) {
    filteredRules = ruleOptions[1].list;
  }

  return {
    filteredRules,
    currentFilter: tempFilter,

    typeOptions,
    ruleOptions,
    ppOptions,
    priceTypeOptions,
    priceOptions,
  };
}
